import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent } from '@artstorefronts/ui';
import { Download, Calendar, Copy, Edit, Image, Clapperboard, ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { TranslatedCalling } from './common';
import { InstagramPreview } from '../../InstagramPreview';
import { useMutation, useQuery } from '@apollo/client';
import { UPDATE_IMAGE_MUTATION, CREATE_SOCIAL_POST } from '../../../graphql/mutations';
import { INTEGRATIONS_QUERY } from '../../../graphql/queries';
import { DateTimePicker } from '../../DateTimePicker';
import { AccountSelector } from '../../AccountSelector';
import { toast } from 'sonner';

interface MarketingPackageData {
  title: string;
  description: string;
  colorPalette: string[];
  marketingCopy: {
    tagline: string;
    elevator_pitch: string;
    key_selling_points: string[];
  };
  mockImages: string[];
  carousels: Array<{
    id: string;
    type: string;
    title: string;
    content: string;
    hashtags: string[];
    mediaUrls: string[];
    slideDescriptions: string[];
    engagementHooks: string[];
    postingTips: string;
  }>;
  reels: Array<{
    id: string;
    type: string;
    title: string;
    description: string;
    hashtags: string[];
    inputProps: {
      images: Array<{
        url: string;
        text: string;
        zoomDirection: string;
        panDirection: string;
        textBackgroundColor: string;
      }>;
      secondsPerImage: number;
      framesPerImage: number;
    };
    musicSuggestion: string;
  }>;
  userBrandVoice: {
    artistName: string;
    brandVoice: string;
    voiceTones: string[];
    voiceFormalities: string[];
  };
  generatedAt: string;
  packageStats: {
    totalAssets: number;
    mockupCount: number;
    carouselCount: number;
    reelCount: number;
  };
}

type TabType = 'text' | 'images' | 'social';

function CopyToClipboardButton({ text, label }: { text: string; label: string }) {
  const { t } = useTranslation('chat');
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    toast.success(t('tool.generate-marketing-package.copied-to-clipboard'));
  };
  return (
    <Button variant="outline" onClick={handleCopy}>
      <Copy className="w-4 h-4 mr-2" />
      {label}
    </Button>
  );
}

function TabNavigation({
  activeTab,
  onTabChange,
  currentIndex,
  totalCount,
  onPrev,
  onNext,
}: {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  currentIndex: number;
  totalCount: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const tabs = [
    { id: 'text' as TabType, icon: Edit },
    { id: 'images' as TabType, icon: Image },
    { id: 'social' as TabType, icon: Clapperboard },
  ];

  return (
    <div className="flex items-center justify-between mb-8">
      <div className="flex gap-3">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                activeTab === tab.id ? 'bg-black text-white' : 'text-black hover:text-gray-600'
              }`}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onPrev} disabled={currentIndex === 0}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onNext} disabled={currentIndex === totalCount - 1}>
          <ChevronRight className="w-4 h-4" />
        </Button>
        <span className="text-xs text-gray-400">
          {currentIndex + 1}/{totalCount}
        </span>
      </div>
    </div>
  );
}

function CardStack({ children, currentIndex }: { children: React.ReactNode[]; currentIndex: number }) {
  if (!children.length) return null;

  return <div>{children[currentIndex]}</div>;
}

function TextCard({
  title,
  content,
  imageId,
  fieldName,
}: {
  title: string;
  content: string;
  imageId?: string;
  fieldName?: 'title' | 'description';
}) {
  const { t } = useTranslation('chat');
  const [updateImageMutation] = useMutation(UPDATE_IMAGE_MUTATION);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!imageId || !fieldName) {
      toast.error(t('tool.generate-marketing-package.cannot-save'));
      return;
    }

    setSaving(true);
    try {
      await updateImageMutation({
        variables: {
          input: {
            id: imageId,
            [fieldName]: content,
          },
        },
      });
      toast.success(t('tool.generate-marketing-package.saved-to-artwork', { title }));
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(t('tool.generate-marketing-package.failed-to-save', { title: title.toLowerCase() }));
    } finally {
      setSaving(false);
    }
  };

  const showSaveButton = imageId && fieldName && (fieldName === 'title' || fieldName === 'description');

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-lg">{title}</h3>
      <div className="text-gray-700 leading-relaxed max-w-2xl">{content}</div>
      <div className="flex gap-2 mt-6">
        {showSaveButton && (
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving
              ? t('tool.generate-marketing-package.saving')
              : t('tool.generate-marketing-package.save-to-artwork')}
          </Button>
        )}
        <CopyToClipboardButton text={content} label={t('tool.generate-marketing-package.copy')} />
      </div>
    </div>
  );
}

function ImageCard({ src, alt }: { src: string; alt: string }) {
  const { t } = useTranslation('chat');
  return (
    <div className="space-y-6">
      <img src={src} alt={alt} className="max-w-xs object-contain rounded-lg" />
      <Button onClick={() => window.open(src, '_blank')}>
        <Download className="w-4 h-4 mr-2" />
        {t('tool.generate-marketing-package.download')}
      </Button>
    </div>
  );
}

function SocialCard({ item, type }: { item: any; type: 'carousel' | 'reel' }) {
  const { t } = useTranslation('chat');
  const [showScheduling, setShowScheduling] = useState(false);
  const [scheduledFor, setScheduledFor] = useState<Date | null>(null);
  const [selectedUserIntegration, setSelectedUserIntegration] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: integrationsData, loading: integrationsLoading } = useQuery(INTEGRATIONS_QUERY);

  const [createSocialPost] = useMutation(CREATE_SOCIAL_POST, {
    onCompleted: () => {
      toast.success(t('tool.generate-marketing-package.type-scheduled', { type }));
      setShowScheduling(false);
      setScheduledFor(null);
      setSelectedUserIntegration(null);
    },
    onError: (error) => {
      toast.error(t('tool.generate-marketing-package.error-scheduling', { type, message: error.message }));
      setIsSubmitting(false);
    },
  });

  const activeIntegrations =
    integrationsData?.integrations?.filter((integration: any) =>
      integration.userIntegrations?.some((ui: any) => ui.status === 'active')
    ) || [];

  useEffect(() => {
    if (!selectedUserIntegration && !integrationsLoading && activeIntegrations.length > 0) {
      const allUserIntegrations = activeIntegrations.flatMap((integration: any) =>
        integration.userIntegrations.map((ui: any) => ui)
      );

      if (allUserIntegrations.length > 0) {
        setSelectedUserIntegration(allUserIntegrations[0]);
      }
    }
  }, [activeIntegrations, selectedUserIntegration, integrationsLoading]);

  const handleScheduleClick = () => {
    setShowScheduling(true);
  };

  const handleSubmitSchedule = async () => {
    if (!scheduledFor || !selectedUserIntegration) {
      toast.error(t('tool.common.select-date-and-account'));
      return;
    }

    setIsSubmitting(true);

    try {
      const isReel = type === 'reel';
      const captionText = `${item.title}\n\n${item.content || item.description}\n\n${item.hashtags.join(' ')}`;

      await createSocialPost({
        variables: {
          input: {
            userIntegrationId: selectedUserIntegration.id,
            content: captionText,
            mediaUrls: isReel ? [item.videoUrl] : item.mediaUrls,
            scheduledFor: scheduledFor.toISOString(),
            reel: isReel,
          },
        },
      });
    } catch (error) {
      console.error(`Error scheduling ${type}:`, error);
      setIsSubmitting(false);
    }
  };

  const isReel = type === 'reel';
  const caption = item.content || item.description;

  return (
    <div className="space-y-6">
      <div className="w-[320px]">
        <InstagramPreview
          username={selectedUserIntegration?.metadata?.username || 'artist'}
          profilePicture={selectedUserIntegration?.metadata?.profilePictureUrl}
          images={isReel ? [] : item.mediaUrls}
          caption={caption}
          skipImagePreload={isReel}
          isReel={isReel}
          inputProps={isReel ? item.inputProps : undefined}
        />
      </div>

      {!showScheduling && (
        <Button onClick={handleScheduleClick}>
          <Calendar className="w-4 h-4 mr-2" />
          {t('tool.common.schedule')}
        </Button>
      )}

      {showScheduling && (
        <div className="space-y-4 max-w-[400px]">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('tool.common.account')}</label>
            <AccountSelector
              integrations={activeIntegrations}
              loading={integrationsLoading}
              selectedUserIntegration={selectedUserIntegration}
              onSelect={setSelectedUserIntegration}
            />
          </div>

          {activeIntegrations.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('tool.common.date-and-time')}</label>
              <div className="w-full">
                <DateTimePicker date={scheduledFor} onDateChange={setScheduledFor} disablePastDates={true} />
              </div>
            </div>
          )}

          {activeIntegrations.length > 0 && (
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowScheduling(false)}
                className="flex-1 text-sm"
                disabled={isSubmitting}
              >
                {t('tool.common.cancel')}
              </Button>
              <Button
                onClick={handleSubmitSchedule}
                disabled={!scheduledFor || !selectedUserIntegration || isSubmitting}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white border-0 text-sm"
              >
                {isSubmitting ? t('tool.common.scheduling') : t('tool.common.schedule')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MarketingPackageResult({ data, imageId }: { data: MarketingPackageData; imageId?: string }) {
  const { t } = useTranslation('chat');
  const [activeTab, setActiveTab] = useState<TabType>('text');
  const [textIndex, setTextIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);
  const [socialIndex, setSocialIndex] = useState(0);

  if (!data) return <div>{t('tool.generate-marketing-package.no-data')}</div>;
  if (!data.title || !data.description) return <div>{t('tool.generate-marketing-package.invalid-data')}</div>;

  const textCards = [
    {
      title: t('tool.generate-marketing-package.artwork-title'),
      content: data.title,
      type: 'Title',
      fieldName: 'title' as const,
    },
    {
      title: t('tool.generate-marketing-package.marketing-description'),
      content: data.description,
      type: 'Description',
      fieldName: 'description' as const,
    },
    { title: t('tool.generate-marketing-package.tagline'), content: data.marketingCopy.tagline, type: 'Tagline' },
    {
      title: t('tool.generate-marketing-package.elevator-pitch'),
      content: data.marketingCopy.elevator_pitch,
      type: 'Pitch',
    },
    {
      title: t('tool.generate-marketing-package.selling-points'),
      content: data.marketingCopy.key_selling_points.join('\n• '),
      type: 'Points',
    },
  ];

  const socialCards = [
    ...data.carousels.map((carousel) => ({ ...carousel, cardType: 'carousel' as const })),
    ...data.reels.map((reel) => ({ ...reel, cardType: 'reel' as const })),
  ];

  const getCurrentData = () => {
    switch (activeTab) {
      case 'text':
        return { items: textCards, currentIndex: textIndex, setIndex: setTextIndex };
      case 'images':
        return { items: data.mockImages, currentIndex: imageIndex, setIndex: setImageIndex };
      case 'social':
        return { items: socialCards, currentIndex: socialIndex, setIndex: setSocialIndex };
      default:
        return { items: [], currentIndex: 0, setIndex: () => {} };
    }
  };

  const currentData = getCurrentData();

  const handlePrev = () => currentData.setIndex(Math.max(0, currentData.currentIndex - 1));
  const handleNext = () => currentData.setIndex(Math.min(currentData.items.length - 1, currentData.currentIndex + 1));

  return (
    <div className="w-full max-w-none mb-20">
      <Card className="shadow-xs">
        <CardContent className="pt-3 px-6 pb-6">
          <TabNavigation
            activeTab={activeTab}
            onTabChange={setActiveTab}
            currentIndex={currentData.currentIndex}
            totalCount={currentData.items.length}
            onPrev={handlePrev}
            onNext={handleNext}
          />

          {activeTab === 'text' && (
            <CardStack currentIndex={textIndex}>
              {textCards.map((card, index) => (
                <TextCard
                  key={index}
                  title={card.title}
                  content={card.content}
                  imageId={imageId}
                  fieldName={card.fieldName}
                />
              ))}
            </CardStack>
          )}

          {activeTab === 'images' && (
            <CardStack currentIndex={imageIndex}>
              {data.mockImages.map((src, index) => (
                <ImageCard
                  key={index}
                  src={src}
                  alt={t('tool.generate-marketing-package.room-mockup-alt', { number: index + 1 })}
                />
              ))}
            </CardStack>
          )}

          {activeTab === 'social' && (
            <CardStack currentIndex={socialIndex}>
              {socialCards.map((item, index) => (
                <SocialCard key={index} item={item} type={item.cardType} />
              ))}
            </CardStack>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const generate_marketing_package = {
  type: 'acting',
  call: () => <TranslatedCalling i18nKey="tool.generate-marketing-package.generating" />,
  'partial-call': () => <TranslatedCalling i18nKey="tool.generate-marketing-package.creating-assets" />,
  result: (toolCall: any) => {
    const data = toolCall.result as MarketingPackageData;
    const imageId = toolCall.args?.imageId;
    return <MarketingPackageResult data={data} imageId={imageId} />;
  },
};
